import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";
import colors from "../../theme/color/ColorTheme";
import FilledButton from "../button/FilledButton";
import ProductCard from "./ProductCard";

const GalleryWrapper = styled.div`
  position: relative;
  width: calc(100% - 50px);
  overflow: hidden;
  padding: 20px 0;
  margin: auto;
`;

const CardsContainer = styled.div`
  width: fit-content;
  display: flex;
  gap: 40px;
  transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transform: translateX(${(props) => props.translateX}px);
  will-change: transform;
`;

const CardWrapper = styled.div`
  flex: 0 0 auto;
  width: 300px;
  /* opacity: ${(props) => (props.isVisible ? 1 : 0.3)}; */
  /* transform: scale(${(props) => (props.isVisible ? 1 : 0.95)}); */
  transition: opacity 0.4s ease, transform 0.4s ease;
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: ${colors.white || "#ffffff"};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.disabled ? 0.3 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  &:hover:not(:disabled) {
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  ${(props) =>
    props.direction === "left" &&
    css`
      left: 10px;
    `}

  ${(props) =>
    props.direction === "right" &&
    css`
      right: 10px;
    `}

  svg {
    width: 20px;
    height: 20px;
    fill: ${colors.black || "#000000"};
  }
`;

const Center = styled.div`
  width: fit-content;
  margin: auto;
  margin-top: 15px;
`;
const GalleryName = styled.h2`
  text-align: center;
  padding: 60px 0px;
`;

function ProductGallery({
  products = [],
  galleryName,
  currentProductId,
  navigateTo,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(7);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const defaultProducts = Array.from({ length: 7 }, (_, i) => ({
    id: i + 1,
    name: `Product ${i + 1}`,
    price: `$${(Math.random() * 100 + 20).toFixed(0)}`,
  }));

  const productList = products.length > 0 ? products : defaultProducts;

  useEffect(() => {
    const updateVisibleCards = () => {
      const width = window.innerWidth;
      if (width < 768) setVisibleCards(2);
      else if (width < 1024) setVisibleCards(2);
      else if (width < 1200) setVisibleCards(3);
      else if (width > 1300) setVisibleCards(3);
    };

    updateVisibleCards();
    window.addEventListener("resize", updateVisibleCards);
    return () => window.removeEventListener("resize", updateVisibleCards);
  }, []);

  const maxVisibleCards = Math.min(visibleCards, productList.length);
  const maxIndex =
    productList.length <= maxVisibleCards
      ? 0
      : productList.length - maxVisibleCards;
  const cardWidth = 340; // 300px card + 40px gap
  const translateX = -currentIndex * cardWidth;

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  const isCardVisible = (cardIndex) => {
    return (
      cardIndex >= currentIndex && cardIndex < currentIndex + maxVisibleCards
    );
  };

  return (
    <>
      <GalleryWrapper ref={containerRef}>
        {galleryName && <GalleryName>{galleryName}</GalleryName>}

        <ArrowButton
          direction="left"
          onClick={handlePrevious}
          disabled={currentIndex === 0 || productList.length <= maxVisibleCards}
        >
          <svg viewBox="0 0 24 24">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </ArrowButton>
        <CardsContainer translateX={translateX}>
          {productList
            .filter((product) => product._id !== currentProductId)
            .map((product, index) => (
              <CardWrapper
                key={product._id || index}
                isVisible={isCardVisible(index)}
              >
                <ProductCard product={product} />
              </CardWrapper>
            ))}
        </CardsContainer>
        <ArrowButton
          direction="right"
          onClick={handleNext}
          disabled={
            currentIndex >= maxIndex || productList.length <= maxVisibleCards
          }
        >
          <svg viewBox="0 0 24 24">
            <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
          </svg>
        </ArrowButton>
      </GalleryWrapper>
      <Center>
        <FilledButton
          text={"View More"}
          onClick={() => {
            navigate(navigateTo && navigateTo);
          }}
        />
      </Center>
    </>
  );
}

export default ProductGallery;
